import {
  type Component,
  createSignal,
  createResource,
  Show,
  For,
  createMemo
} from "solid-js"
import { useParams, useNavigate } from "@solidjs/router"
import type { WorkplaceId, WorkerId, Worker } from "@eckersdorf/api"
import {
  getWorkplaces,
  getWorkers,
  createWorker,
  updateWorker,
  deleteWorker
} from "../lib/api"
import { useAuth } from "../lib/auth"

export const WorkersPage: Component = () => {
  const { user } = useAuth()
  const params = useParams()
  const navigate = useNavigate()

  const selectedWorkplaceId = createMemo(() => params['workplaceId'] as WorkplaceId | undefined)

  // Fetch workplaces
  const [workplaces] = createResource(user, async (u) => {
    if (!u) return []
    return getWorkplaces()
  })

  // Fetch workers
  const [workers, { refetch }] = createResource(
    () => ({ user: user(), workplaces: workplaces(), filterId: selectedWorkplaceId() }),
    async ({ user: u, workplaces: ws, filterId }) => {
      if (!u || !ws || ws.length === 0) return []
      
      if (filterId) {
        return getWorkers(filterId)
      }
      
      // Fetch all
      try {
        const results = await Promise.all((ws ?? []).map(w => getWorkers(w.id)))
        // We might want to attach workplace info if it's missing in worker object, 
        // but assuming API returns standard Worker object which usually has workplaceId.
        return results.flat()
      } catch (e) {
        console.error("Failed to fetch all workers", e)
        return []
      }
    },
    { initialValue: [] }
  )

  // Modal & Form State
  const [isModalOpen, setIsModalOpen] = createSignal(false)
  const [editingId, setEditingId] = createSignal<WorkerId | null>(null)
  const [submitting, setSubmitting] = createSignal(false)
  
  const [formData, setFormData] = createSignal({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    position: "seller",
    workingHours: 8,
    workplaceId: ""
  })

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      position: "seller",
      workingHours: 8,
      workplaceId: selectedWorkplaceId() ?? ((workplaces() ?? [])[0]?.id ?? "")
    })
    setEditingId(null)
  }

  const openAddModal = () => {
    resetForm()
    setIsModalOpen(true)
  }

  const openEditModal = (worker: Worker) => {
    console.log("Opening edit modal for worker:", worker)
    setFormData({
      firstName: worker.firstName,
      lastName: worker.lastName,
      email: worker.email,
      phoneNumber: worker.phoneNumber ?? "",
      position: worker.position ?? "seller",
      workingHours: worker.workingHours,
      workplaceId: worker.workplaceId
    })
    setEditingId(worker.id)
    setIsModalOpen(true)
  }

  const handleDelete = async (worker: Worker) => {
    if (!confirm(`Czy na pewno chcesz usunąć pracownika ${worker.firstName} ${worker.lastName}?`)) {
      return
    }
    try {
      await deleteWorker(worker.id)
      await refetch()
    } catch (e) {
      alert("Nie udało się usunąć pracownika")
      console.error(e)
    }
  }

  const handleSubmit = async (e: Event) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      const data = formData()
      // Basic validation
      if (!data.workplaceId) {
        alert("Wybierz sklep")
        return
      }

      const input = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email as any,
        phoneNumber: data.phoneNumber as any,
        position: data.position,
        workingHours: Number(data.workingHours),
        workplaceId: data.workplaceId as WorkplaceId,
        address: null,
        birthDate: null
      }

      if (editingId()) {
        await updateWorker(editingId()!, input)
      } else {
        await createWorker(input)
      }
      
      setIsModalOpen(false)
      await refetch()
    } catch (e) {
      console.error(e)
      alert("Błąd zapisu")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div class="space-y-6">
      {/* Header / Filter */}
      <div class="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div class="flex items-center gap-4 w-full sm:w-auto">
          <label class="font-medium text-gray-700 whitespace-nowrap">Filtruj sklep:</label>
          <select
            value={selectedWorkplaceId() ?? ""}
            onChange={(e) => {
              const val = e.currentTarget.value
              if (val) navigate(`/workers/${val}`)
              else navigate(`/workers`)
            }}
            class="flex-1 sm:w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Wszystkie sklepy</option>
            <For each={workplaces()}>
              {(wp) => <option value={wp.id}>{wp.name}</option>}
            </For>
          </select>
        </div>

        <button
          onClick={openAddModal}
          class="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors font-medium"
        >
          Dodaj pracownika
        </button>
      </div>

      {/* Table */}
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pracownik</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kontakt</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stanowisko</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sklep</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Akcje</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <Show when={!workers.loading} fallback={
                 <tr><td colspan="5" class="px-6 py-4 text-center">Ładowanie...</td></tr>
              }>
                 <For each={workers()} fallback={
                    <tr><td colspan="5" class="px-6 py-4 text-center text-gray-500">Brak pracowników</td></tr>
                 }>
                   {(worker) => (
                     <tr class="hover:bg-gray-50">
                       <td class="px-6 py-4 whitespace-nowrap">
                         <div class="text-sm font-medium text-gray-900">{worker.firstName} {worker.lastName}</div>
                       </td>
                       <td class="px-6 py-4 whitespace-nowrap">
                         <div class="text-sm text-gray-500">{worker.email}</div>
                         <div class="text-sm text-gray-500">{worker.phoneNumber}</div>
                       </td>
                       <td class="px-6 py-4 whitespace-nowrap">
                         <div class="text-sm text-gray-900">{worker.position}</div>
                         <div class="text-xs text-gray-500">{worker.workingHours}h</div>
                       </td>
                       <td class="px-6 py-4 whitespace-nowrap">
                         <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                           {(workplaces() ?? []).find(wp => wp.id === worker.workplaceId)?.name ?? worker.workplaceId}
                         </span>
                       </td>
                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button 
                            type="button"
                            onClick={(e) => { e.preventDefault(); openEditModal(worker); }}
                            class="text-blue-600 hover:text-blue-900 mr-4"
                          >
                            Edytuj
                          </button>
                          <button 
                            type="button"
                            onClick={(e) => { e.preventDefault(); handleDelete(worker); }}
                            class="text-red-600 hover:text-red-900"
                          >
                            Usuń
                          </button>
                        </td>
                     </tr>
                   )}
                 </For>
              </Show>
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      <Show when={isModalOpen()}>
        <div class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setIsModalOpen(false)}></div>

            <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full">
              <form onSubmit={handleSubmit}>
                <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4" id="modal-title">
                    {editingId() ? "Edytuj pracownika" : "Dodaj pracownika"}
                  </h3>
                  
                  <div class="grid grid-cols-1 gap-4">
                    <div class="grid grid-cols-2 gap-4">
                      <div>
                        <label class="block text-sm font-medium text-gray-700">Imię</label>
                        <input 
                          type="text" 
                          required
                          value={formData().firstName}
                          onInput={(e) => setFormData({...formData(), firstName: e.currentTarget.value})}
                          class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border p-2"
                        />
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-700">Nazwisko</label>
                        <input 
                          type="text" 
                          required
                          value={formData().lastName}
                          onInput={(e) => setFormData({...formData(), lastName: e.currentTarget.value})}
                          class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border p-2"
                        />
                      </div>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-700">Email</label>
                      <input 
                        type="email" 
                        required
                        value={formData().email}
                        onInput={(e) => setFormData({...formData(), email: e.currentTarget.value})}
                        class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border p-2"
                      />
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-700">Telefon</label>
                      <input 
                        type="text" 
                        value={formData().phoneNumber}
                        onInput={(e) => setFormData({...formData(), phoneNumber: e.currentTarget.value})}
                        class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border p-2"
                      />
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                      <div>
                        <label class="block text-sm font-medium text-gray-700">Stanowisko</label>
                        <select 
                          value={formData().position}
                          onChange={(e) => setFormData({...formData(), position: e.currentTarget.value})}
                          class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border p-2"
                        >
                          <option value="seller">Sprzedawca</option>
                          <option value="butcher">Masarz</option>
                          <option value="driver">Kierowca</option>
                          <option value="office">Biuro</option>
                        </select>
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-700">Godziny (dziennie)</label>
                        <input 
                          type="number" 
                          min="1" max="12"
                          value={formData().workingHours}
                          onInput={(e) => setFormData({...formData(), workingHours: Number(e.currentTarget.value)})}
                          class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border p-2"
                        />
                      </div>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-700">Sklep</label>
                      <select 
                        required
                        value={formData().workplaceId}
                        onChange={(e) => setFormData({...formData(), workplaceId: e.currentTarget.value})}
                        class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border p-2"
                      >
                        <option value="" disabled>Wybierz sklep</option>
                        <For each={workplaces()}>
                          {(wp) => <option value={wp.id}>{wp.name}</option>}
                        </For>
                      </select>
                    </div>
                  </div>
                </div>
                <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button 
                    type="submit" 
                    disabled={submitting()}
                    class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {submitting() ? "Zapisywanie..." : "Zapisz"}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setIsModalOpen(false)}
                    class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Anuluj
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Show>
    </div>
  )
}
